import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Avatar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Stack,
  Paper,
  AvatarGroup,
  LinearProgress,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  Comment as CommentIcon,
  AccessTime as AccessTimeIcon,
  MoreHoriz as MoreHorizIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Image as ImageIcon,
  Send as SendIcon,
  CloudUpload as CloudUploadIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  VerifiedUser as VerifiedUserIcon,
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useSelector, useDispatch } from 'react-redux';
import { getAllActiveEmployeesAPICall } from "../../../../../../API/Staff/HRM/employeeAPI";

// --- Helpers ---
const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const baseUrl = axios.defaults.baseURL?.replace(/\/$/, '') || '';
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
};

// --- Sub-Components ---

const TaskCard = ({ task, onClick }) => {
  const commentCount = task.comments?.length || 0;
  const attachmentCount = task.attachments?.length || 0;
  const daysLeft = task.dueDate ? Math.ceil((new Date(task.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 0;
  
  // Get all image attachments (limit to 3)
  const imageAttachments = task.attachments?.filter(url => url.match(/\.(jpeg|jpg|gif|png)$/i)) || [];
  const displayImages = imageAttachments.slice(0, 3);

  const getBorderColor = () => {
    switch(task.status) {
        case 'PENDING': return '#29b6f6';
        case 'IN_PROGRESS': return '#26c6da';
        case 'IN_REVIEW': return '#ffca28';
        case 'COMPLETED': return '#ef5350';
        case 'REJECTED': return '#c62828';
        default: return '#bdbdbd';
    }
  };

  return (
    <Card 
      onClick={() => onClick(task)}
      sx={{ 
        mb: 2, 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' },
        borderLeft: `5px solid ${getBorderColor()}`,
        overflow: 'visible',
        position: 'relative'
      }}
    >
      {/* Rejection Icon */}
      {task.status === 'REJECTED' && (
          <Box sx={{ position: 'absolute', top: -10, right: -10, zIndex: 1 }}>
              <Tooltip title="Task Rejected">
                  <Avatar sx={{ width: 28, height: 28, bgcolor: '#c62828', border: '2px solid white' }}>
                      <CloseIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Avatar>
              </Tooltip>
          </Box>
      )}

      {/* Images Grid */}
      {displayImages.length > 0 && (
        <Box sx={{ display: 'flex', height: 140, overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
            {displayImages.map((img, idx) => (
                <Box 
                    key={idx}
                    component="img" 
                    src={getImageUrl(img)} 
                    sx={{ 
                        width: `${100 / displayImages.length}%`, 
                        height: '100%', 
                        objectFit: 'cover',
                        borderRight: idx < displayImages.length - 1 ? '1px solid white' : 'none'
                    }} 
                />
            ))}
        </Box>
      )}

      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Tags */}
        <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" gap={0.5}>
          {(task.tags || []).map((tag, idx) => (
            <Chip 
                key={idx} 
                label={tag} 
                size="small" 
                sx={{ 
                    borderRadius: 1.5, 
                    fontSize: '0.7rem', 
                    height: 22, 
                    bgcolor: '#f5f7fa',
                    color: '#5c6b7f',
                    fontWeight: 600,
                    border: '1px solid #eef0f2'
                }} 
            />
          ))}
        </Stack>

        <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 1, color: '#2d3436', lineHeight: 1.4 }}>
          {task.title}
        </Typography>
        
        {task.description && (
            <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                    mb: 2, 
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 3,
                    fontSize: '0.85rem',
                    lineHeight: 1.5
                }}
            >
                {task.description}
            </Typography>
        )}

        {/* Subtask Progress */}
        {task.subtasks?.length > 0 && (
             <Box sx={{ mb: 2 }}>
                 <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">Progress</Typography>
                    <Typography variant="caption" fontWeight="bold">
                        {Math.round((task.subtasks.filter(s=>s.isCompleted).length / task.subtasks.length) * 100)}%
                    </Typography>
                 </Stack>
                 <LinearProgress 
                    variant="determinate" 
                    value={(task.subtasks.filter(s=>s.isCompleted).length / task.subtasks.length) * 100} 
                    sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f2f5', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: getBorderColor() } }}
                 />
             </Box>
        )}

        <Divider sx={{ my: 1.5, borderColor: '#f0f2f5' }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
             {/* Due Date */}
             {task.dueDate && (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: daysLeft < 0 ? '#ef5350' : '#95a5a6' }} />
                    <Typography variant="caption" fontWeight="500" color={daysLeft < 0 ? 'error' : 'text.secondary'}>
                        {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </Typography>
                </Box>
             )}
             
             {/* Comments & Attachments Counts */}
             {(commentCount > 0 || attachmentCount > 0) && (
                 <Stack direction="row" spacing={1.5}>
                    {commentCount > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                            <CommentIcon sx={{ fontSize: 16, color: '#bdc3c7' }} />
                            <Typography variant="caption" fontWeight="600" color="#95a5a6">{commentCount}</Typography>
                        </Box>
                    )}
                    {attachmentCount > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                            <AttachFileIcon sx={{ fontSize: 16, color: '#bdc3c7' }} rotate={30} />
                            <Typography variant="caption" fontWeight="600" color="#95a5a6">{attachmentCount}</Typography>
                        </Box>
                    )}
                 </Stack>
             )}
          </Stack>
          
          {/* Assignee Avatar */}
          <Tooltip title={`Assigned to: ${task.assignedTo?.staff_name || 'Unassigned'}`}>
            <Avatar 
                src={getImageUrl(task.assignedTo?.imageUrl)} 
                alt={task.assignedTo?.staff_name}
                sx={{ width: 32, height: 32, border: '2px solid white', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }} 
            />
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
};

const KanbanColumn = ({ title, tasks, color, count, icon: Icon, onTaskClick, onAddClick }) => (
  <Box sx={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column' }}>
    {/* Header */}
    <Paper 
      elevation={0}
      sx={{ 
        p: 2.5, 
        mb: 3, 
        bgcolor: color, 
        borderRadius: 3, 
        color: 'white', 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}
    >
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Typography variant="h6" fontWeight="700" sx={{ letterSpacing: 0.5 }}>{title}</Typography>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '50%', p: 0.5, display: 'flex' }}>
                <Icon sx={{ fontSize: 20 }} />
            </Box>
        </Stack>
        <Typography variant="h4" fontWeight="800">{count}</Typography>
      </Box>
      {onAddClick ? (
        <IconButton 
            size="small" 
            onClick={onAddClick} 
            sx={{ 
                color: color, 
                bgcolor: 'white', 
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                width: 40,
                height: 40
            }}
        >
          <AddIcon />
        </IconButton>
      ) : (
          <Icon sx={{ fontSize: 60, opacity: 0.2 }} />
      )}
    </Paper>

    {/* Task List */}
    <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1, pb: 2, mx: -1 }}>
      {tasks.map(task => (
        <TaskCard key={task._id} task={task} onClick={onTaskClick} />
      ))}
      {tasks.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, opacity: 0.5 }}>
              <Typography variant="body2">No tasks</Typography>
          </Box>
      )}
    </Box>
  </Box>
);

// --- Main Component ---

export const EmployeeTasks = ({ singleEmpData }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Filters
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Helper for Images
  const getImageUrl = (path) => {
      if (!path) return null;
      if (path.startsWith('http')) return path;
      const baseUrl = axios.defaults.baseURL?.replace(/\/$/, '') || '';
      const cleanPath = path.startsWith('/') ? path : `/${path}`;
      return `${baseUrl}${cleanPath}`;
  };
  
  // File Upload Ref
  const fileInputRef = useRef(null);

  // Filter State
  const [showMyTasksOnly, setShowMyTasksOnly] = useState(true);

  // New Task / Edit Task State
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    subtasks: [],
    attachments: [],
    tags: [],
    priority: 'MEDIUM',
    startDate: '',
    dueDate: '',
    assignedTo: '', 
    newSubtask: '',
    newTag: '',
    newComment: ''
  });

  const currentEmpId = localStorage.getItem("EMP_ID");
  const targetEmployeeId = singleEmpData?._id || currentEmpId;
  const currentUserRole = useSelector((state) => state.userRoleSlice?.value);
  
  // Employee List for Assignee Dropdown
  const allEmployees = useSelector((state) => state.allActiveEmployeeSlice.value);
  const dispatch = useDispatch();

  useEffect(() => {
    // If no singleEmpData (Standalone mode), fetch all employees to populate dropdown
    if (!singleEmpData && (!allEmployees || allEmployees.length === 0)) {
       getAllActiveEmployeesAPICall();
    }
  }, [singleEmpData, allEmployees]);

  useEffect(() => {
    if (targetEmployeeId) {
      fetchTasks();
    }
  }, [targetEmployeeId, showMyTasksOnly]);

  // Clear selected employee filter when switching to "My Tasks"
  useEffect(() => {
    if (showMyTasksOnly) {
        setSelectedEmployee(null);
    }
  }, [showMyTasksOnly]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const payload = {};
      
      if (singleEmpData) {
          payload.employeeId = singleEmpData._id;
      } else {
          if (showMyTasksOnly) {
              payload.employeeId = currentEmpId;
          } else {
              payload.filter = 'ALL';
          }
      }

      // console.log('🔍 Fetching Tasks. showMyTasksOnly:', showMyTasksOnly, 'currentEmpId:', currentEmpId, 'Payload:', payload);

      // Force fresh data by bypassing cache
      const response = await axios.post(`/api/task/my-tasks?t=${new Date().getTime()}`, payload, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (response.data.success) {
        const fetchedTasks = response.data.data;
        // console.log('✅ Tasks Fetched:', fetchedTasks.length);
        setTasks(fetchedTasks);
      } else {
        console.error('❌ API returned success: false');
      }
    } catch (error) {
      console.error("Error fetching tasks", error);
    } finally {
      setLoading(false);
    }
  };

  // Date Helpers
  const isSameDay = (d1, d2) => {
      const date1 = new Date(d1);
      const date2 = new Date(d2);
      return date1.getFullYear() === date2.getFullYear() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate();
  };

  const handlePrevDay = () => {
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() - 1);
      setSelectedDate(newDate);
  };

  const handleNextDay = () => {
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() + 1);
      setSelectedDate(newDate);
  };

  const handleEmployeeClick = (empId) => {
      if (selectedEmployee === empId) {
          setSelectedEmployee(null); // Deselect
      } else {
          setSelectedEmployee(empId);
      }
  };

  // Group Tasks with Filters
  const filteredTasks = tasks.filter(t => {
      // Date Filter: Show if selected date is within Start-Due range, or matches Due Date
      const checkDate = new Date(selectedDate);
      checkDate.setHours(0,0,0,0);
      
      if (t.startDate && t.dueDate) {
          const start = new Date(t.startDate); start.setHours(0,0,0,0);
          const end = new Date(t.dueDate); end.setHours(0,0,0,0);
          if (checkDate < start || checkDate > end) return false;
      } else if (t.dueDate) {
          const due = new Date(t.dueDate); due.setHours(0,0,0,0);
          if (checkDate.getTime() !== due.getTime()) return false;
      }
      
      // Employee Filter (Avatar Click)
      if (selectedEmployee) {
          const assignedId = t.assignedTo?._id || t.assignedTo;
          if (assignedId !== selectedEmployee) return false;
      }

      return true;
  });

  const pendingTasks = filteredTasks.filter(t => t.status === 'PENDING' || t.status === 'REJECTED');
  const inProgressTasks = filteredTasks.filter(t => t.status === 'IN_PROGRESS' || t.status === 'ACCEPTED');
  const inReviewTasks = filteredTasks.filter(t => t.status === 'IN_REVIEW');
  const completedTasks = filteredTasks.filter(t => t.status === 'COMPLETED');

  // Handlers
  const handleOpenCreate = () => {
    setSelectedTask(null);
    setEditForm({
      title: '',
      description: '',
      subtasks: [],
      attachments: [],
      tags: [],
      priority: 'MEDIUM',
      startDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      assignedTo: singleEmpData?._id || currentEmpId,
      newSubtask: '',
      newTag: '',
      newComment: ''
    });
    setIsEditMode(true);
    setOpenModal(true);
  };

  const handleOpenDetail = (task) => {
    if (task.status === 'REJECTED') {
        Swal.fire({
            title: 'Task Rejected',
            html: `<p style="color: #666;">Reason:</p><h4 style="color: #d32f2f;">${task.rejectionReason || 'No reason provided'}</h4>`,
            icon: 'error',
            showCancelButton: true,
            confirmButtonText: 'Fix Task',
            cancelButtonText: 'Close',
            confirmButtonColor: '#29b6f6'
        }).then((result) => {
            if (result.isConfirmed) {
                openTaskModal(task);
            }
        });
        return;
    }
    openTaskModal(task);
  };

  const openTaskModal = (task) => {
    setSelectedTask(task);
    setEditForm({
      ...task,
      assignedTo: task.assignedTo?._id || task.assignedTo,
      startDate: task.startDate ? new Date(task.startDate).toISOString().split('T')[0] : '',
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
      newSubtask: '',
      newTag: '',
      newComment: ''
    });
    setIsEditMode(false);
    setOpenModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        assignedTo: editForm.assignedTo,
        startDate: editForm.startDate,
        dueDate: editForm.dueDate,
        priority: editForm.priority,
        tags: editForm.tags || [],
        subtasks: editForm.subtasks || [],
        attachments: editForm.attachments || [],
        coverImage: editForm.coverImage
      };

      // console.log('💾 Saving task with payload:', payload);

      let response;
      if (selectedTask) {
        // Update
        response = await axios.put(`/api/task/update/${selectedTask._id}`, payload);
      } else {
        // Create
        response = await axios.post('/api/task/create', payload);
      }

      if (response.data.success) {
        setOpenModal(false);
        
        // Jump to task date to ensure visibility
        if (payload.startDate) {
            const newDate = new Date(payload.startDate);
            // Reset time to avoid timezone shifts if just date string
            const offsetDate = new Date(newDate.valueOf() + newDate.getTimezoneOffset() * 60000);
            setSelectedDate(new Date(payload.startDate)); 
        }

        fetchTasks();
        Swal.fire({
            icon: 'success',
            title: 'Saved',
            text: 'Task saved successfully',
            timer: 1500,
            showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Save error:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to save task', 'error');
    }
  };

  const handleAddSubtask = () => {
    if (!editForm.newSubtask.trim()) return;
    setEditForm(prev => ({
      ...prev,
      subtasks: [...(prev.subtasks || []), { title: prev.newSubtask, isCompleted: false }],
      newSubtask: ''
    }));
  };
  
  const handleAddTag = () => {
    if (!editForm.newTag.trim()) return;
    if ((editForm.tags || []).includes(editForm.newTag.trim())) {
        setEditForm(prev => ({ ...prev, newTag: '' }));
        return;
    }
    setEditForm(prev => ({
        ...prev,
        tags: [...(prev.tags || []), prev.newTag.trim()],
        newTag: ''
    }));
  };

  const handleToggleSubtask = (index) => {
    const newSubtasks = [...editForm.subtasks];
    newSubtasks[index].isCompleted = !newSubtasks[index].isCompleted;
    setEditForm(prev => ({ ...prev, subtasks: newSubtasks }));
  };

  const handleDeleteSubtask = (index) => {
    const newSubtasks = editForm.subtasks.filter((_, i) => i !== index);
    setEditForm(prev => ({ ...prev, subtasks: newSubtasks }));
  };

  const handleDeleteAttachment = (index) => {
    const newAttachments = editForm.attachments.filter((_, i) => i !== index);
    setEditForm(prev => ({ ...prev, attachments: newAttachments }));
  };

  // File Upload
  const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if(!file) return;

      const formData = new FormData();
      formData.append('file', file);

      try {
          const res = await axios.post('/api/task/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
          });
          if(res.data.success) {
              setEditForm(prev => ({
                  ...prev,
                  attachments: [...(prev.attachments || []), res.data.url]
              }));
          }
      } catch (error) {
          Swal.fire('Error', 'File upload failed', 'error');
      }
  };

  const handleAddComment = async () => {
    if (!selectedTask || !editForm.newComment.trim()) return;
    try {
        await axios.put(`/api/task/progress/${selectedTask._id}`, {
            comment: editForm.newComment
        });
        // Refresh details
        const response = await axios.post('/api/task/my-tasks', { employeeId: targetEmployeeId, filter: 'ALL' }); // Fetch all to be safe
        const updatedTask = response.data.data.find(t => t._id === selectedTask._id);
        if(updatedTask) {
            setSelectedTask(updatedTask);
            // Update local tasks state too
            setTasks(prev => prev.map(t => t._id === updatedTask._id ? updatedTask : t));
            setEditForm(prev => ({ ...prev, newComment: '' }));
        }
    } catch(e) { console.error(e); }
  };

  const handleUpdateStatus = async (newStatus) => {
      if(!selectedTask) return;
      
      if (newStatus === 'REJECTED') {
          setRejectionOpen(true);
          return;
      }
      await updateStatusAPI(newStatus);
  }

  const updateStatusAPI = async (status, reason = '') => {
      try {
          await axios.put(`/api/task/status/${selectedTask._id}`, { 
              status,
              rejectionReason: reason 
            });
          setOpenModal(false);
          fetchTasks();
          Swal.fire({
            icon: 'success',
            title: 'Updated',
            text: `Task moved to ${status.replace('_', ' ')}`,
            timer: 1500,
            showConfirmButton: false
          });
      } catch(e) { console.error(e); }
  }

  const handleConfirmRejection = () => {
      if(!rejectionReason.trim()) return;
      updateStatusAPI('REJECTED', rejectionReason);
      setRejectionOpen(false);
      setRejectionReason('');
  }

  // Render Modal Content
  const renderModalContent = () => (
    <Grid container spacing={4}>
      {/* Left Column: Details */}
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          placeholder="Task Title"
          variant="standard"
          value={editForm.title}
          onChange={(e) => setEditForm({...editForm, title: e.target.value})}
          InputProps={{ sx: { fontSize: '1.8rem', fontWeight: 800, color: '#2d3436' } }}
          sx={{ mb: 3 }}
        />
        
        <Typography variant="subtitle2" fontWeight="700" color="text.secondary" sx={{ mb: 1 }}>DESCRIPTION</Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Add a detailed description..."
          value={editForm.description}
          onChange={(e) => setEditForm({...editForm, description: e.target.value})}
          sx={{ 
            mb: 4, 
            bgcolor: '#f9fafb', 
            borderRadius: 2, 
            '& fieldset': { border: '1px solid #eef0f2' },
            '&:hover fieldset': { borderColor: '#dfe6e9' }
          }}
        />

        {/* Subtasks */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
             <Typography variant="subtitle2" fontWeight="700" color="text.secondary">
                SUBTASKS ({editForm.subtasks?.filter(s=>s.isCompleted).length}/{editForm.subtasks?.length})
             </Typography>
          </Stack>
          
          <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
            <List dense disablePadding>
                {(editForm.subtasks || []).map((subtask, idx) => (
                <ListItem 
                    key={idx} 
                    divider={idx !== editForm.subtasks.length - 1}
                    secondaryAction={
                        <IconButton edge="end" size="small" onClick={() => handleDeleteSubtask(idx)}>
                            <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                    }
                    sx={{ bgcolor: subtask.isCompleted ? '#f9fafb' : 'white' }}
                >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox 
                            edge="start" 
                            checked={subtask.isCompleted} 
                            onChange={() => handleToggleSubtask(idx)}
                            icon={<RadioButtonUncheckedIcon color="disabled" />}
                            checkedIcon={<CheckCircleIcon color="success" />}
                        />
                    </ListItemIcon>
                    <ListItemText 
                        primary={subtask.title} 
                        primaryTypographyProps={{ 
                            sx: { 
                                textDecoration: subtask.isCompleted ? 'line-through' : 'none',
                                color: subtask.isCompleted ? 'text.disabled' : 'text.primary'
                            } 
                        }} 
                    />
                </ListItem>
                ))}
                <ListItem sx={{ bgcolor: '#f9fafb' }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        <IconButton size="small" onClick={handleAddSubtask}>
                            <AddIcon color="primary" />
                        </IconButton>
                    </ListItemIcon>
                    <TextField 
                        variant="standard" 
                        placeholder="Add a new subtask..." 
                        fullWidth 
                        value={editForm.newSubtask}
                        onChange={(e) => setEditForm({...editForm, newSubtask: e.target.value})}
                        onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); handleAddSubtask(); } }}
                        InputProps={{ disableUnderline: true }}
                    />
                </ListItem>
            </List>
          </Paper>
        </Box>

        {/* Attachments */}
        <Box sx={{ mb: 4 }}>
             <Typography variant="subtitle2" fontWeight="700" color="text.secondary" sx={{ mb: 2 }}>ATTACHMENTS</Typography>
             <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                 {(editForm.attachments || []).map((url, idx) => (
                     <Box key={idx} sx={{ position: 'relative' }}>
                         <Box 
                            component="a" 
                            href={getImageUrl(url)} 
                            target="_blank" 
                            sx={{ 
                                width: 100, 
                                height: 100, 
                                borderRadius: 3, 
                                bgcolor: '#f0f2f5', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                textDecoration: 'none',
                                border: '1px solid #e1e4e8',
                                overflow: 'hidden'
                            }}
                         >
                             {url.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                                 <Box component="img" src={getImageUrl(url)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                             ) : (
                                 <AttachFileIcon color="action" />
                             )}
                         </Box>
                         <IconButton 
                            size="small"
                            onClick={() => handleDeleteAttachment(idx)}
                            sx={{ 
                                position: 'absolute', 
                                top: -8, 
                                right: -8, 
                                bgcolor: 'white', 
                                border: '1px solid #eee',
                                width: 24,
                                height: 24,
                                boxShadow: 1,
                                '&:hover': { bgcolor: '#ffcdd2' }
                            }}
                         >
                            <CloseIcon fontSize="small" color="error" sx={{ fontSize: 14 }} />
                         </IconButton>
                     </Box>
                 ))}
                 <Box 
                    sx={{ 
                        width: 100, 
                        height: 100, 
                        borderRadius: 3, 
                        border: '2px dashed #ced6e0', 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#a4b0be', bgcolor: '#f1f2f6' }
                    }}
                    onClick={() => fileInputRef.current.click()}
                 >
                     <CloudUploadIcon color="disabled" sx={{ mb: 0.5 }} />
                     <Typography variant="caption" color="text.secondary">Upload</Typography>
                 </Box>
                 <input type="file" hidden ref={fileInputRef} onChange={handleFileUpload} />
             </Stack>
        </Box>

        {/* Comments */}
        {selectedTask && (
            <Box>
                <Typography variant="subtitle2" fontWeight="700" color="text.secondary" sx={{ mb: 2 }}>COMMENTS</Typography>
                <List sx={{ mb: 2, maxHeight: 300, overflowY: 'auto', bgcolor: '#f9fafb', borderRadius: 2, p: 2 }}>
                    {(selectedTask.comments || []).length === 0 && (
                        <Typography variant="body2" color="text.secondary" align="center">No comments yet.</Typography>
                    )}
                    {(selectedTask.comments || []).map((c, i) => (
                        <ListItem key={i} alignItems="flex-start" sx={{ px: 0 }}>
                            <ListItemAvatar>
                                <Avatar src={getImageUrl(c.user?.imageUrl)} sx={{ width: 36, height: 36, mt: 0.5, border: '2px solid white', boxShadow: 1 }} />
                            </ListItemAvatar>
                             <Paper elevation={0} sx={{ bgcolor: 'white', p: 2, borderRadius: '0 16px 16px 16px', maxWidth: '80%', border: '1px solid #eee' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="subtitle2" fontWeight="bold">{c.user?.staff_name || 'User'}</Typography>
                                    <Typography variant="caption" color="text.secondary" ml={2}>{new Date(c.createdAt).toLocaleString()}</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.primary">{c.text}</Typography>
                             </Paper>
                        </ListItem>
                    ))}
                </List>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 40, height: 40 }} src={getImageUrl(singleEmpData?.imageUrl)} />
                    <TextField 
                        fullWidth 
                        placeholder="Write a comment..." 
                        variant="outlined"
                        size="medium" 
                        value={editForm.newComment}
                        onChange={(e) => setEditForm({...editForm, newComment: e.target.value})}
                        InputProps={{
                            sx: { borderRadius: 3, bgcolor: 'white' },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleAddComment} disabled={!editForm.newComment.trim()} color="primary">
                                        <SendIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Stack>
            </Box>
        )}

      </Grid>

      {/* Right Column: Meta */}
      <Grid item xs={12} md={4} sx={{ borderLeft: { md: '1px solid #f0f2f5' }, pl: { md: 4 } }}>
        <Stack spacing={4}>
            
            {/* Actions (if editing) */}
            {selectedTask && (
                <Box>
                     <Typography variant="subtitle2" fontWeight="700" color="text.secondary" gutterBottom>STATUS</Typography>
                     <Grid container spacing={1}>
                        {['PENDING', 'IN_PROGRESS', 'IN_REVIEW', 'COMPLETED', 'REJECTED'].map(status => (
                            <Grid item xs={6} key={status}>
                                <Button 
                                    variant={selectedTask.status === status ? "contained" : "outlined"}
                                    size="small" 
                                    fullWidth 
                                    color={
                                        status === 'COMPLETED' ? 'success' : 
                                        status === 'REJECTED' ? 'error' : 
                                        status === 'IN_REVIEW' ? 'warning' : 'primary'
                                    }
                                    onClick={() => handleUpdateStatus(status)}
                                    disabled={selectedTask.status === status}
                                    sx={{ fontSize: '0.7rem', borderRadius: 2, textTransform: 'capitalize' }}
                                >
                                    {status.replace('_', ' ').toLowerCase()}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            <Box>
                <Typography variant="subtitle2" fontWeight="700" color="text.secondary" gutterBottom>ASSIGNEE</Typography>
                {!singleEmpData && allEmployees ? (
                    <FormControl fullWidth size="small">
                        <Select
                            value={editForm.assignedTo}
                            onChange={(e) => setEditForm({...editForm, assignedTo: e.target.value})}
                            displayEmpty
                            sx={{ borderRadius: 2, bgcolor: '#f9fafb' }}
                        >
                            <MenuItem value="" disabled>Select Employee</MenuItem>
                            {allEmployees.map(emp => (
                                <MenuItem key={emp._id} value={emp._id}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Avatar src={getImageUrl(emp.imageUrl)} sx={{ width: 24, height: 24 }} />
                                        <Typography variant="body2">{emp.staff_name}</Typography>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#f9fafb' }}>
                        <Avatar src={getImageUrl(singleEmpData?.imageUrl)} sx={{ width: 32, height: 32 }} />
                        <Typography variant="body2" fontWeight="600">{singleEmpData?.staff_name}</Typography>
                    </Paper>
                )}
            </Box>

            {selectedTask && (
                <Box>
                    <Typography variant="subtitle2" fontWeight="700" color="text.secondary" gutterBottom>ASSIGNED BY</Typography>
                     <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#f9fafb', borderColor: '#eef0f2' }}>
                        <Avatar src={getImageUrl(selectedTask.assignedBy?.imageUrl)} sx={{ width: 24, height: 24 }} />
                        <Typography variant="body2" fontWeight="600">{selectedTask.assignedBy?.staff_name || 'Unknown'}</Typography>
                     </Paper>
                </Box>
            )}

            <Box>
                <Typography variant="subtitle2" fontWeight="700" color="text.secondary" gutterBottom>DUE DATE</Typography>
                <TextField 
                    type="date" 
                    fullWidth 
                    variant="outlined" 
                    size="small" 
                    value={editForm.dueDate}
                    onChange={(e) => setEditForm({...editForm, dueDate: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f9fafb' } }}
                />
            </Box>

            <Box>
                <Typography variant="subtitle2" fontWeight="700" color="text.secondary" gutterBottom>PRIORITY</Typography>
                <FormControl fullWidth size="small">
                    <Select
                        value={editForm.priority}
                        onChange={(e) => setEditForm({...editForm, priority: e.target.value})}
                        sx={{ borderRadius: 2, bgcolor: '#f9fafb' }}
                    >
                        <MenuItem value="LOW"><Chip label="Low Priority" size="small" color="success" variant="outlined" icon={<TrendingUpIcon />} /></MenuItem>
                        <MenuItem value="MEDIUM"><Chip label="Medium Priority" size="small" color="warning" variant="outlined" icon={<TrendingUpIcon />} /></MenuItem>
                        <MenuItem value="HIGH"><Chip label="High Priority" size="small" color="error" variant="outlined" icon={<TrendingUpIcon />} /></MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box>
                 <Typography variant="subtitle2" fontWeight="700" color="text.secondary" gutterBottom>TAGS</Typography>
                 <Stack direction="row" flexWrap="wrap" gap={1}>
                     {(editForm.tags || []).map((tag, idx) => (
                         <Chip 
                            key={idx} 
                            label={tag} 
                            onDelete={() => {
                                const newTags = editForm.tags.filter((_, i) => i !== idx);
                                setEditForm({...editForm, tags: newTags});
                            }} 
                            sx={{ borderRadius: 1 }}
                         />
                     ))}
                 </Stack>
                 <Stack direction="row" spacing={1} mt={1}>
                    <TextField 
                        placeholder="New tag"
                        size="small"
                        fullWidth
                        value={editForm.newTag}
                        onChange={(e) => setEditForm({...editForm, newTag: e.target.value})}
                        onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button variant="outlined" sx={{ minWidth: 'auto', borderRadius: 2 }} onClick={handleAddTag}>
                        <AddIcon />
                    </Button>
                 </Stack>
            </Box>
            
        </Stack>
      </Grid>
    </Grid>
  );

  // --- Main Render ---
  return (
    <Box sx={{ p: 3, height: '100%', bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" mb={4} spacing={2}>
        
        {/* Left: Title & Avatars */}
        <Stack direction="row" alignItems="center" spacing={3}>
            <Box>
                <Typography variant="h4" fontWeight="800" color="#2d3436">
                    Task Board
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Manage projects and track progress
                </Typography>
            </Box>
            
            <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center' }} />
            
            {/* Team Members Filter */}
            <AvatarGroup max={6} sx={{ '& .MuiAvatar-root': { width: 36, height: 36, fontSize: '0.8rem', border: '2px solid white', cursor: 'pointer' } }}>
                {singleEmpData ? (
                    <Avatar src={getImageUrl(singleEmpData.imageUrl)} alt={singleEmpData.staff_name} />
                ) : (
                    (allEmployees || []).slice(0, 8).map(emp => (
                        <Tooltip key={emp._id} title={`Filter by ${emp.staff_name}`}>
                            <Avatar 
                                src={getImageUrl(emp.imageUrl)} 
                                alt={emp.staff_name} 
                                onClick={() => handleEmployeeClick(emp._id)}
                                sx={{ 
                                    border: selectedEmployee === emp._id ? '2px solid #ff7043' : '2px solid white',
                                    opacity: selectedEmployee && selectedEmployee !== emp._id ? 0.5 : 1
                                }}
                            />
                        </Tooltip>
                    ))
                )}
            </AvatarGroup>
        </Stack>
        
        {/* Center: Date Nav */}
        <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderRadius: 5, border: '1px solid #e0e0e0' }}>
            <IconButton size="small" onClick={handlePrevDay}><ChevronLeftIcon /></IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="subtitle2" fontWeight="600">
                    {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
                {isSameDay(selectedDate, new Date()) && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Today</Typography>}
            </Box>
            <IconButton size="small" onClick={handleNextDay}><ChevronRightIcon /></IconButton>
        </Paper>

        {/* Right: Actions */}
        <Stack direction="row" alignItems="center" spacing={2}>
            <FormControlLabel 
                control={
                    <Switch 
                        checked={showMyTasksOnly} 
                        onChange={(e) => setShowMyTasksOnly(e.target.checked)} 
                        color="primary"
                    />
                }
                label={<Typography variant="body2" fontWeight="600">Show my tasks only</Typography>}
            />
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={handleOpenCreate}
                sx={{ 
                    borderRadius: 3, 
                    textTransform: 'none', 
                    bgcolor: '#ff7043', 
                    px: 3,
                    py: 1,
                    fontWeight: 'bold',
                    boxShadow: '0 4px 14px rgba(255, 112, 67, 0.4)',
                    '&:hover': { bgcolor: '#f4511e', boxShadow: '0 6px 20px rgba(255, 112, 67, 0.6)' } 
                }}
            >
                Add Task
            </Button>
        </Stack>
      </Stack>

      {/* Kanban Board */}
      <Box sx={{ display: 'flex', gap: 4, overflowX: 'auto', pb: 4, minHeight: 'calc(100vh - 200px)' }}>
        <KanbanColumn 
            title="To Do" 
            count={pendingTasks.length} 
            tasks={pendingTasks} 
            color="#29b6f6" 
            icon={AssignmentIcon}
            onTaskClick={handleOpenDetail}
            onAddClick={handleOpenCreate}
        />
        <KanbanColumn 
            title="In Progress" 
            count={inProgressTasks.length} 
            tasks={inProgressTasks} 
            color="#26c6da" 
            icon={TrendingUpIcon}
            onTaskClick={handleOpenDetail} 
        />
        <KanbanColumn 
            title="In Review" 
            count={inReviewTasks.length} 
            tasks={inReviewTasks} 
            color="#ffca28" 
            icon={VisibilityIcon}
            onTaskClick={handleOpenDetail} 
        />
        <KanbanColumn 
            title="Done" 
            count={completedTasks.length} 
            tasks={completedTasks} 
            color="#ef5350" 
            icon={VerifiedUserIcon}
            onTaskClick={handleOpenDetail} 
        />
      </Box>
      
      {/* Detail/Create Modal */}
      <Dialog 
        open={openModal} 
        onClose={() => setOpenModal(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, height: '90vh' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 3, borderBottom: '1px solid #f0f2f5', alignItems: 'center' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ bgcolor: '#e3f2fd', p: 1, borderRadius: 2 }}>
                    <AssignmentIcon color="primary" />
                </Box>
                <Box>
                    <Typography variant="h6" fontWeight="bold">{selectedTask ? 'Task Details' : 'Create New Task'}</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedTask ? `ID: #${selectedTask._id.slice(-6)}` : 'Fill in the details below'}</Typography>
                </Box>
            </Stack>
            <Box>
                <Button onClick={handleSave} variant="contained" sx={{ mr: 2, borderRadius: 3, px: 4, fontWeight: 'bold' }}>Save Changes</Button>
                <IconButton onClick={() => setOpenModal(false)} sx={{ bgcolor: '#f5f5f5' }}><CloseIcon /></IconButton>
            </Box>
        </Box>
        <DialogContent sx={{ p: 4, bgcolor: 'white' }}>
            {renderModalContent()}
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionOpen} onClose={() => setRejectionOpen(false)}>
          <Box sx={{ p: 3, minWidth: 300 }}>
              <Typography variant="h6" gutterBottom>Reject Task</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                  Please provide a reason for rejecting this task.
              </Typography>
              <TextField
                  autoFocus
                  fullWidth
                  multiline
                  rows={3}
                  label="Rejection Reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  variant="outlined"
              />
              <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
                  <Button onClick={() => setRejectionOpen(false)}>Cancel</Button>
                  <Button 
                    variant="contained" 
                    color="error" 
                    onClick={handleConfirmRejection}
                    disabled={!rejectionReason.trim()}
                  >
                      Reject
                  </Button>
              </Stack>
          </Box>
      </Dialog>
    </Box>
  );
};
